import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { useAuth } from "./hooks/useAuth";
import { IMAGES } from "@/constants/images";
import { HelmetProvider } from "react-helmet-async";

// Lazy load components for code splitting
const AppLayout = lazy(() => import("./components/layout/AppLayout").then(module => ({ default: module.AppLayout })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Orders = lazy(() => import("./pages/Orders"));
const ProductCosts = lazy(() => import("./pages/ProductCosts"));
const ProductMargins = lazy(() => import("./pages/ProductMargins"));
const ProductSourcing = lazy(() => import("./pages/ProductSourcing"));
const ProductUserSourcing = lazy(() => import("./pages/ProductUserSourcing"));
const TrendingSource = lazy(() => import("./pages/TrendingSource"));
const BulkDiscount = lazy(() => import("./pages/BulkDiscount"));
const FlashDeal = lazy(() => import("./pages/FlashDeal"));
const CampaignPricing = lazy(() => import("./pages/CampaignPricing"));
const ShopeeConnection = lazy(() => import("./pages/ShopeeConnection"));
const Profile = lazy(() => import("./pages/Profile"));
const Billing = lazy(() => import("./pages/Billing"));
const Coupon = lazy(() => import("./pages/Coupon"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Index = lazy(() => import("./pages/Index"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Price = lazy(() => import("./pages/Price"));
const Checkout = lazy(() => import("./pages/Checkout"));
const ChromeExtensionDownload = lazy(() => import("./pages/ChromeExtensionDownload"));

// Guide pages
const GuideDashboard = lazy(() => import("./pages/guides/GuideDashboard"));
const GuideProductMargins = lazy(() => import("./pages/guides/GuideProductMargins"));
const GuideProductCosts = lazy(() => import("./pages/guides/GuideProductCosts"));
const GuideProductBoost = lazy(() => import("./pages/guides/GuideProductBoost"));
const GuideBulkDiscount = lazy(() => import("./pages/guides/GuideBulkDiscount"));
const GuideCampaignPricing = lazy(() => import("./pages/guides/GuideCampaignPricing"));
const GuideShopeeConnection = lazy(() => import("./pages/guides/GuideShopeeConnection"));
const GuideProductSourcing = lazy(() => import("./pages/guides/GuideProductSourcing"));
const GuideProductUserSourcing = lazy(() => import("./pages/guides/GuideProductUserSourcing"));
const GuideTrendingSource = lazy(() => import("./pages/guides/GuideTrendingSource"));
const GuideFlashDeal = lazy(() => import("./pages/guides/GuideFlashDeal"));
const GuideVideos = lazy(() => import("./pages/guides/GuideVideos"));

const queryClient = new QueryClient();

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center space-y-6">
          <img 
            src={IMAGES.logo.loadingSplashKo}
            alt="셀잇파파 로딩" 
            className="w-96 h-96 mx-auto"
          />
          <div className="space-y-3">
            <h2 className="text-2xl font-semibold text-gray-800">인증 확인 중...</h2>
            <p className="text-gray-600">셀잇파파에 로그인하고 있습니다</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="text-center space-y-6">
      <img 
        src={IMAGES.logo.loadingSplashKo}
        alt="셀잇파파 로딩" 
        className="w-96 h-96 mx-auto"
      />
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-gray-800">페이지 로딩 중...</h2>
        <p className="text-gray-600">잠시만 기다려주세요</p>
      </div>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Price />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/chrome-extension-download" element={<ChromeExtensionDownload />} />
            {/* failUrl은 /pricing?billing=failed 로 처리 → 전용 라우트 제거 */}
            
            {/* Checkout Route - Protected */}
            <Route path="/checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            
            {/* Guide Pages - Public Access */}
            <Route path="/guide/dashboard" element={<GuideDashboard />} />
            <Route path="/guide/product-margins" element={<GuideProductMargins />} />
            <Route path="/guide/product-costs" element={<GuideProductCosts />} />
            <Route path="/guide/product-boost" element={<GuideProductBoost />} />
            <Route path="/guide/bulk-discount" element={<GuideBulkDiscount />} />
            <Route path="/guide/campaign-pricing" element={<GuideCampaignPricing />} />
            <Route path="/guide/shopee-connection" element={<GuideShopeeConnection />} />
            <Route path="/guide/product-sourcing" element={<GuideProductSourcing />} />
            <Route path="/guide/product-user-sourcing" element={<GuideProductUserSourcing />} />
            <Route path="/guide/trending-source" element={<GuideTrendingSource />} />
            <Route path="/guide/flash-deal" element={<GuideFlashDeal />} />
            <Route path="/guide/videos" element={<GuideVideos />} />
            
            {/* Protected Routes with Layout */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <Orders />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/product-costs" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductCosts />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/product-margins" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductMargins />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/bulk-discount" element={
              <ProtectedRoute>
                <AppLayout>
                  <BulkDiscount />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/flash-deal" element={
              <ProtectedRoute>
                <AppLayout>
                  <FlashDeal />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/campaign-pricing" element={
              <ProtectedRoute>
                <AppLayout>
                  <CampaignPricing />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/product-sourcing" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductSourcing />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/user-source" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductUserSourcing />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/trending-source" element={
              <ProtectedRoute>
                <AppLayout>
                  <TrendingSource />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/shopee-connection" element={
              <ProtectedRoute>
                <AppLayout>
                  <ShopeeConnection />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <AppLayout>
                  <Profile />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/billing" element={
              <ProtectedRoute>
                <AppLayout>
                  <Billing />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/coupon" element={
              <ProtectedRoute>
                <AppLayout>
                  <Coupon />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
